/**
 * EdgZoomToolbar — the React Flow port of `components/Paper/index.js`'s
 * `PaperZoomToolbar` (phase 6b).
 *
 * Reuses the SAME `Toolbar` UI component (variant="floating") and the SAME
 * zoom icons (`ZoomIn` / `ZoomOut` / `ZoomCenter`) the old EDG used, so the
 * floating zoom control looks identical. Only the wiring changes: instead of the
 * Rappid `ScrollablePaper` it drives React Flow's viewport via `useReactFlow`.
 *
 * Behaviour parity with `ScrollablePaper` (the JointJS PaperScroller):
 *  - zoomIn:   +0.2 absolute step
 *  - zoomOut:  -0.2 step, only while zoom >= 0.3 (matches `canZoomOut` + the
 *              guard in ScrollablePaper.zoomOut)
 *  - zoom100:  absolute 1.0 ("100%")
 *  - center:   pan so the content midpoint sits in the pane centre, without
 *              changing zoom (PaperScroller `centerContent` — not `zoomToFit`)
 *  - min zoom 0.3 (the floor used everywhere in the old EDG / paper.js)
 *
 * The live zoom percentage is read from React Flow's internal store
 * (`useStore` selecting `transform[2]`) so it updates on wheel-zoom and pan-zoom
 * too, not just button clicks — same observable feel as `usePaperZoom`'s state.
 */
import React, { useCallback } from 'react';
import { useReactFlow, useStore } from '@xyflow/react';
import Toolbar from '../components/Toolbar';
import {
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  ZoomCenter as ZoomCenterIcon,
} from '../components/Icons';
import { centerContent } from './centerContent';

export const MIN_ZOOM = 0.3;
export const MAX_ZOOM = 4;
const ZOOM_STEP = 0.2;

export type EdgZoomToolbarProps = {
  /** Class for the floating toolbar (matches the old `floatingToolbarClassName`). */
  className?: string;
  /** Name used in the "Center ???" tooltip (the old EDG passed "EDG"). */
  name?: string;
};

export default function EdgZoomToolbar({ className, name = 'EDG' }: EdgZoomToolbarProps) {
  const { zoomTo, getZoom, getNodes, getNodesBounds, setCenter } = useReactFlow();

  // Live zoom from React Flow's store (transform === [x, y, zoom]).
  const zoom = useStore((s) => s.transform[2]);

  const zoomIn = useCallback(() => {
    zoomTo(Math.min(MAX_ZOOM, getZoom() + ZOOM_STEP));
  }, [zoomTo, getZoom]);

  const zoomOut = useCallback(() => {
    const current = getZoom();
    if (current >= MIN_ZOOM) {
      zoomTo(Math.max(MIN_ZOOM, current - ZOOM_STEP));
    }
  }, [zoomTo, getZoom]);

  const zoom100 = useCallback(() => {
    zoomTo(1);
  }, [zoomTo]);

  const center = useCallback(() => {
    const nodes = getNodes();
    centerContent({
      getNodes: () => nodes,
      getNodesBounds: () => getNodesBounds(nodes),
      getZoom,
      setCenter,
    });
  }, [getNodes, getNodesBounds, getZoom, setCenter]);

  const zoomPercent = (zoom * 100).toFixed(0);
  const canZoomOut = zoom >= MIN_ZOOM;

  const buttons = [
    {
      key: 'zoom_out',
      icon: ZoomOutIcon,
      handler: zoomOut,
      active: false,
      title: 'Zoom out',
      disabled: !canZoomOut,
    },
    {
      key: '100_percent',
      text: `${zoomPercent}%`,
      handler: zoom100,
      active: false,
      title: 'Zoom to 100%',
      disabled: false,
    },
    {
      key: 'zoom_in',
      icon: ZoomInIcon,
      handler: zoomIn,
      active: false,
      title: 'Zoom in',
      disabled: false,
    },
    {
      key: 'center',
      icon: ZoomCenterIcon,
      handler: center,
      active: false,
      title: `Center ${name}`,
      disabled: false,
    },
  ];

  return <Toolbar buttons={buttons} variant="floating" className={className} />;
}
